import { useEffect, useState } from "react";
import Select from "react-select";
import { FaDownload, FaFilter, FaUpload } from "react-icons/fa";
import FormCheckbox from "../../shared/FormCheckbox";
import { customStyles } from "../../../utils/styleUtils";
import { CATEGORIES, DATA_TYPES, SCOPES } from "./fieldForm";

const CONTROL_HEIGHT = 40;

const selectStyles = {
    ...customStyles,
    control: (base, state) => ({
        ...customStyles.control(base, state),
        minHeight: CONTROL_HEIGHT,
        height: CONTROL_HEIGHT,
    }),
    valueContainer: (base) => ({
        ...base,
        height: CONTROL_HEIGHT - 2,
        padding: "0 8px",
    }),
    indicatorsContainer: (base) => ({
        ...base,
        height: CONTROL_HEIGHT - 2,
    }),
    menuPortal: (base) => ({ ...base, zIndex: 200 }),
};

const menuPortal = typeof document !== "undefined" ? document.body : null;

const optionFor = (options, value) => options.find((option) => option.value === value) ?? null;

const categoryOptions = CATEGORIES.map((category) => ({ value: category, label: category }));
const dataTypeOptions = DATA_TYPES.map((dataType) => ({ value: dataType, label: dataType }));
const scopeOptions = SCOPES.map((scope) => ({ value: scope.value, label: scope.label }));
const universalOptions = [
    { value: "true", label: "Universal" },
    { value: "false", label: "Not universal" },
];

const FilterSelect = ({ id, label, options, value, onChange, isLoading, isDisabled }) => (
    <div className="cdes-field-toolbar__field">
        <label htmlFor={id}>{label}</label>
        <Select
            inputId={id}
            className="react-select-container"
            classNamePrefix="react-select"
            placeholder="All"
            options={options}
            value={optionFor(options, value)}
            onChange={(option) => onChange(option?.value || "")}
            isClearable
            isLoading={isLoading}
            isDisabled={isDisabled}
            menuPortalTarget={menuPortal}
            menuPlacement="auto"
            styles={selectStyles}
        />
    </div>
);

const columnFilterCount = (filters) =>
    [filters.category, filters.dataType, filters.scope, filters.universal, filters.documentType].filter(Boolean).length;

const FieldToolbar = ({
    filters,
    documentTypes,
    documentTypesLoading,
    control,
    exporting,
    onFilter,
    onInactive,
    onClear,
    onCreate,
    onImport,
    onExport,
}) => {
    const [searchDraft, setSearchDraft] = useState(filters.q);
    const [showMoreFilters, setShowMoreFilters] = useState(false);
    const documentTypeOptions = documentTypes.map((documentType) => ({
        value: documentType.documentTypeKey,
        label: documentType.displayName,
    }));
    const activeColumnFilters = columnFilterCount(filters);

    useEffect(() => {
        setSearchDraft(filters.q);
    }, [filters.q]);

    const applySearch = () => onFilter("q", searchDraft.trim());

    return (
        <>
            <div className="cdes-grid__header">
                <div>
                    <p className="title">Fields</p>
                    <p className="sub-title">Configure catalog fields, document-type assignments, and import or export the catalog.</p>
                </div>
                <div className="cdes-grid__header-actions">
                    <button type="button" className="edit-btn cdes-grid__btn" onClick={onCreate}>
                        + Add Field
                    </button>
                    <button type="button" className="outline-btn cdes-grid__btn" onClick={onImport}>
                        <FaUpload />
                        Import
                    </button>
                    <button type="button" className="outline-btn cdes-grid__btn" onClick={onExport} disabled={exporting}>
                        {exporting ? <span className="loader loader--inline" /> : <FaDownload />}
                        {exporting ? "Exporting…" : "Export"}
                    </button>
                </div>
            </div>
            <div className="cdes-field-toolbar">
                <div className="cdes-field-toolbar__field cdes-field-toolbar__field--search">
                    <label htmlFor="cdes-field-search">Search</label>
                    <div className="cdes-field-toolbar__search-combo">
                        <input
                            id="cdes-field-search"
                            className="cdes-field-toolbar__search"
                            type="search"
                            value={searchDraft}
                            onChange={(event) => {
                                const next = event.target.value;
                                setSearchDraft(next);
                                onFilter("q", next);
                            }}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                    event.preventDefault();
                                    applySearch();
                                }
                            }}
                            placeholder="Name, key, or label"
                        />
                        <button type="button" className="cdes-field-toolbar__search-btn" onClick={applySearch}>
                            Search
                        </button>
                    </div>
                </div>
                <div className="cdes-field-toolbar__inactive">
                    <FormCheckbox name="includeInactive" label="Show inactive fields" control={control} onSelectedHandler={onInactive} />
                </div>
                {showMoreFilters ? (
                    <div className="cdes-field-toolbar__filters" role="region" aria-label="More filters">
                        <FilterSelect
                            id="cdes-field-filter-category"
                            label="Category"
                            options={categoryOptions}
                            value={filters.category}
                            onChange={(value) => onFilter("category", value)}
                        />
                        <FilterSelect
                            id="cdes-field-filter-type"
                            label="Data type"
                            options={dataTypeOptions}
                            value={filters.dataType}
                            onChange={(value) => onFilter("dataType", value)}
                        />
                        <FilterSelect
                            id="cdes-field-filter-scope"
                            label="Scope"
                            options={scopeOptions}
                            value={filters.scope}
                            onChange={(value) => onFilter("scope", value)}
                        />
                        <FilterSelect
                            id="cdes-field-filter-universal"
                            label="Universal"
                            options={universalOptions}
                            value={filters.universal}
                            onChange={(value) => onFilter("universal", value)}
                        />
                        <FilterSelect
                            id="cdes-field-filter-document"
                            label="Document type"
                            options={documentTypeOptions}
                            value={filters.documentType}
                            onChange={(value) => onFilter("documentType", value)}
                            isLoading={documentTypesLoading}
                            isDisabled={documentTypesLoading}
                        />
                    </div>
                ) : null}
                <div className="cdes-field-toolbar__actions">
                    <button
                        type="button"
                        className={`cdes-field-toolbar__text-btn cdes-field-toolbar__text-btn--filter${
                            showMoreFilters ? " cdes-field-toolbar__text-btn--filter-active" : ""
                        }`}
                        onClick={() => setShowMoreFilters((open) => !open)}
                        aria-pressed={showMoreFilters}
                        aria-expanded={showMoreFilters}
                    >
                        <FaFilter aria-hidden />
                        {showMoreFilters ? "Hide filters" : "More filters"}
                        {activeColumnFilters > 0 ? (
                            <span className="cdes-field-toolbar__filter-badge">{activeColumnFilters}</span>
                        ) : null}
                    </button>
                    <button type="button" className="cdes-field-toolbar__text-btn cdes-field-toolbar__text-btn--danger" onClick={onClear}>
                        Clear filters
                    </button>
                </div>
            </div>
        </>
    );
};

export default FieldToolbar;
